import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Search, MapPin, Globe, CheckCircle2,
  Activity, Users, Star, Briefcase, Layout, FileText, Loader2,
} from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import PlacesAutocomplete from '@/components/PlacesAutocomplete';
import ScoreDisplay from '@/components/ScoreDisplay';
import ProfessionSearch from '@/components/ProfessionSearch';
import { useBranding } from '@/context/BrandingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Onboarding Photos ── */
const STEP_PHOTOS = {
  1: 'https://images.unsplash.com/photo-1768428581237-0d4f78bcbcc4?w=800&q=80',
  2: 'https://images.unsplash.com/reserve/oIpwxeeSPy1cnwYpqJ1w_Dufer%20Collateral%20test.jpg?w=800&q=80',
  3: 'https://images.unsplash.com/photo-1761002068605-e27bda0cdc25?w=800&q=80',
  4: 'https://images.unsplash.com/photo-1770855908040-5717c3e13d4a?w=800&q=80',
};

/* ── Social Platforms ── */
const PLATFORMS = [
  { key: 'google_business', name: 'Google Business Profile', icon: Search, placeholder: 'https://google.com/maps/place/Your+Business', example: 'google.com/maps/place/Mikes+Plumbing' },
  { key: 'facebook', name: 'Facebook Business Page', icon: Users, placeholder: 'https://facebook.com/yourbusiness', example: 'facebook.com/mikesplumbing' },
  { key: 'instagram', name: 'Instagram', icon: Layout, placeholder: 'https://instagram.com/yourbusiness', example: 'instagram.com/mikesplumbing' },
  { key: 'twitter', name: 'X (Twitter)', icon: Activity, placeholder: 'https://x.com/yourbusiness', example: 'x.com/mikesplumbing' },
  { key: 'yelp', name: 'Yelp', icon: Star, placeholder: 'https://yelp.com/biz/yourbusiness', example: 'yelp.com/biz/mikes-plumbing-denver' },
  { key: 'linkedin', name: 'LinkedIn', icon: Briefcase, placeholder: 'https://linkedin.com/company/yourbusiness', example: 'linkedin.com/company/mikes-plumbing' },
  { key: 'nextdoor', name: 'Nextdoor', icon: MapPin, placeholder: 'https://nextdoor.com/pages/yourbusiness', example: 'nextdoor.com/pages/mikes-plumbing' },
  { key: 'other', name: 'Other Directory', icon: FileText, placeholder: 'https://...', example: 'thumbtack.com/mikes-plumbing' },
];

const AI_ENGINES = ['ChatGPT', 'Claude', 'Gemini', 'Perplexity', 'Bing AI', 'Grok', 'Alexa AI', 'Siri', 'Meta AI'];

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    business_name: location.state?.businessName || '',
    professions: [],
    city: '',
    state: '',
    zipcode: '',
    website: '',
    social_profiles: {},
  });
  const [scanId, setScanId] = useState(null);
  const [scanStatus, setScanStatus] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const pollRef = useRef(null);

  const totalFormSteps = 4; // Steps 1-4 are form steps, 5=scanning, 6=results

  /* ── Poll scan status ── */
  useEffect(() => {
    if (step !== 5 || !scanId) return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/scan/status/${scanId}`);
        if (!active) return;
        setScanStatus(res.data);
        if (res.data.status === 'completed') {
          const results = await axios.get(`${API}/scan/results/${scanId}`);
          setScanResults(results.data);
          setTimeout(() => setStep(6), 1200);
          return;
        }
        if (res.data.status === 'failed') return;
        pollRef.current = setTimeout(poll, 1500);
      } catch {
        if (active) pollRef.current = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; clearTimeout(pollRef.current); };
  }, [step, scanId]);

  const startScan = async () => {
    setStep(5);
    try {
      const res = await axios.post(`${API}/scan/start`, {
        business_name: formData.business_name,
        professions: formData.professions.map(p => ({ name: p.name, value: p.value, source: p.source, primary: p.primary })),
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipcode,
        website: formData.website,
        social_profiles: formData.social_profiles,
      });
      setScanId(res.data.scan_id);
      localStorage.setItem('gcf_scan_id', res.data.scan_id);
    } catch (err) {
      console.error('Scan start failed:', err);
      setStep(4);
    }
  };

  /* ── Progress bar (2px Brass fill) ── */
  const progress = step <= totalFormSteps ? (step / totalFormSteps) * 100 : 100;

  /* ── Render ── */
  if (step === 5) return <ScanningScreen scanStatus={scanStatus} formData={formData} />;
  if (step === 6 && scanResults) return <ResultsScreen scanResults={scanResults} navigate={navigate} />;

  return (
    <div className="min-h-screen flex flex-col" data-testid="onboarding-page">
      {/* 2px Brass progress bar at top */}
      <div className="fixed top-0 left-0 right-0 z-50 h-[2px]" style={{ backgroundColor: '#D4C9B8' }}>
        <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: '#B8985A' }} />
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-screen">
        {/* LEFT: Photo (hidden on mobile, 50% on desktop) */}
        <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img
              key={step}
              src={STEP_PHOTOS[step] || STEP_PHOTOS[1]}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            />
          </AnimatePresence>
        </div>

        {/* RIGHT: Form */}
        <div className="flex-1 lg:w-1/2 flex flex-col" style={{ backgroundColor: '#F7F4EF' }}>
          {/* Back button */}
          <div className="p-6 flex items-center justify-between">
            {step > 1 ? (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-1 text-sm transition-colors"
                style={{ color: '#8C7E6E' }}
                data-testid="back-btn"
              >
                <ArrowLeft size={16} /> Back
              </button>
            ) : <div />}
            <span className="text-[11px] uppercase tracking-widest" style={{ color: '#8C7E6E' }}>
              Step {step} of {totalFormSteps}
            </span>
          </div>

          {/* Step content */}
          <div className="flex-1 px-6 lg:px-12 pb-32 overflow-y-auto">
            {/* Logo with tagline — centered above questions */}
            <div className="text-center mb-8">
              <OnboardingLogo />
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {step === 1 && <StepBusinessName formData={formData} setFormData={setFormData} onNext={() => setStep(2)} />}
                {step === 2 && <StepProfession formData={formData} setFormData={setFormData} onNext={() => setStep(3)} />}
                {step === 3 && <StepLocation formData={formData} setFormData={setFormData} onNext={() => setStep(4)} />}
                {step === 4 && <StepProfiles formData={formData} setFormData={setFormData} onNext={startScan} onSkip={startScan} />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function OnboardingLogo() {
  const { logos } = useBranding();
  const src = logos.sidebar;
  // Prefer tagline variant; fall back to sidebar logo; fall back to text
  const taglineSrc = '/logos/gcf-logo-tagline-light.svg';
  return (
    <img
      src={src ? taglineSrc : taglineSrc}
      alt="GetChosenFirst"
      className="inline-block"
      style={{ height: '54px' }}
      data-testid="onboarding-logo"
    />
  );
}


/* ══════════════════════════════════════════
   STEP 1: BUSINESS NAME
   ══════════════════════════════════════════ */
function StepBusinessName({ formData, setFormData, onNext }) {
  const canProceed = formData.business_name.trim().length >= 2;

  const handlePlaceSelect = (details) => {
    setFormData(d => ({
      ...d,
      business_name: details.name || d.business_name,
      city: details.city || d.city,
      zipcode: details.zipcode || d.zipcode,
      website: details.website || d.website,
    }));
  };

  return (
    <div data-testid="step-business-name">
      <h2 className="text-[22px] font-semibold mb-2" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
        What is the name of your business?
      </h2>
      <p className="text-sm mb-8" style={{ color: '#8C7E6E' }}>
        We'll use this to search AI engines for your business.
      </p>
      <PlacesAutocomplete
        value={formData.business_name}
        onChange={val => setFormData(d => ({ ...d, business_name: val }))}
        onSelect={handlePlaceSelect}
        placeholder="e.g. Mike's Plumbing"
        testId="business-name-input"
        autoFocus
      />

      <div className="fixed bottom-0 left-0 right-0 lg:left-1/2 p-6" style={{ backgroundColor: 'rgba(247,244,239,0.95)', borderTop: '1px solid #D4C9B8' }}>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="w-full py-4 text-[12px] font-medium uppercase tracking-widest flex items-center justify-center gap-2 transition-opacity disabled:opacity-40"
          style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
          data-testid="next-btn"
        >
          Next <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   STEP 2: PROFESSION
   ══════════════════════════════════════════ */
function StepProfession({ formData, setFormData, onNext }) {
  const canProceed = formData.professions.length > 0;
  return (
    <div data-testid="step-profession">
      <h2 className="text-[22px] font-semibold mb-2" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
        What do you do?
      </h2>
      <p className="text-sm mb-6" style={{ color: '#8C7E6E' }}>
        Search for your profession or type your own.
      </p>
      <ProfessionSearch
        selected={formData.professions}
        onChange={profs => setFormData(d => ({ ...d, professions: profs }))}
      />

      <div className="fixed bottom-0 left-0 right-0 lg:left-1/2 p-6" style={{ backgroundColor: 'rgba(247,244,239,0.95)', borderTop: '1px solid #D4C9B8' }}>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="w-full py-4 text-[12px] font-medium uppercase tracking-widest flex items-center justify-center gap-2 transition-opacity disabled:opacity-40"
          style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
          data-testid="next-btn"
        >
          Next <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   STEP 3: LOCATION + WEBSITE
   ══════════════════════════════════════════ */
function StepLocation({ formData, setFormData, onNext }) {
  const canProceed = formData.city.trim().length >= 2 && formData.zipcode.trim().length >= 5;
  return (
    <div data-testid="step-location">
      <h2 className="text-[22px] font-semibold mb-2" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
        Where are your customers?
      </h2>
      <p className="text-sm mb-6" style={{ color: '#8C7E6E' }}>
        We'll check what AI says about businesses in your area.
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-widest mb-2" style={{ color: '#5C5347' }}>
              City
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={e => setFormData(d => ({ ...d, city: e.target.value }))}
              placeholder="e.g. Denver"
              className="w-full px-5 py-4 text-base outline-none transition-all"
              style={{ backgroundColor: '#FDFBF7', border: '1px solid #D4C9B8', borderRadius: 0, color: '#3C3226' }}
              onFocus={e => e.target.style.borderColor = '#B8985A'}
              onBlur={e => e.target.style.borderColor = '#D4C9B8'}
              data-testid="city-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium uppercase tracking-widest mb-2" style={{ color: '#5C5347' }}>
              Zip Code
            </label>
            <input
              type="text"
              value={formData.zipcode}
              onChange={e => setFormData(d => ({ ...d, zipcode: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
              onKeyDown={e => { if (e.key === 'Enter' && canProceed) onNext(); }}
              placeholder="e.g. 80202"
              className="w-full px-5 py-4 text-base outline-none transition-all"
              style={{ backgroundColor: '#FDFBF7', border: '1px solid #D4C9B8', borderRadius: 0, color: '#3C3226' }}
              onFocus={e => e.target.style.borderColor = '#B8985A'}
              onBlur={e => e.target.style.borderColor = '#D4C9B8'}
              data-testid="zipcode-input"
              inputMode="numeric"
            />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-medium uppercase tracking-widest mb-2" style={{ color: '#5C5347' }}>
            Website <span style={{ color: '#8C7E6E' }}>(optional)</span>
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={e => setFormData(d => ({ ...d, website: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter' && canProceed) onNext(); }}
            placeholder="https://www.yourbusiness.com"
            className="w-full px-5 py-4 text-base outline-none transition-all"
            style={{ backgroundColor: '#FDFBF7', border: '1px solid #D4C9B8', borderRadius: 0, color: '#3C3226' }}
            onFocus={e => e.target.style.borderColor = '#B8985A'}
            onBlur={e => e.target.style.borderColor = '#D4C9B8'}
            data-testid="website-input"
          />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 lg:left-1/2 p-6" style={{ backgroundColor: 'rgba(247,244,239,0.95)', borderTop: '1px solid #D4C9B8' }}>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="w-full py-4 text-[12px] font-medium uppercase tracking-widest flex items-center justify-center gap-2 transition-opacity disabled:opacity-40"
          style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
          data-testid="next-btn"
        >
          Next <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   STEP 4: ONLINE PROFILES
   ══════════════════════════════════════════ */
function StepProfiles({ formData, setFormData, onNext, onSkip }) {
  const [enabled, setEnabled] = useState({});

  const toggle = (key) => {
    setEnabled(prev => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next[key]) {
        setFormData(d => {
          const sp = { ...d.social_profiles };
          delete sp[key];
          return { ...d, social_profiles: sp };
        });
      }
      return next;
    });
  };

  const setUrl = (key, url) => {
    setFormData(d => ({ ...d, social_profiles: { ...d.social_profiles, [key]: url } }));
  };

  const filledCount = Object.values(formData.social_profiles).filter(v => v?.trim()).length;

  return (
    <div data-testid="step-profiles">
      <h2 className="text-[22px] font-semibold mb-2" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
        Where can customers find you online?
      </h2>
      <p className="text-sm mb-6" style={{ color: '#8C7E6E' }}>
        Share your profile links so we scan everything in one pass. You can skip this.
      </p>

      <div className="space-y-2">
        {PLATFORMS.map(p => {
          const Icon = p.icon;
          const isOn = !!enabled[p.key];
          return (
            <div key={p.key} data-testid={`profile-${p.key}`}>
              <button
                onClick={() => toggle(p.key)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all"
                style={{
                  backgroundColor: isOn ? '#FDFBF7' : 'transparent',
                  border: `1px solid ${isOn ? '#B8985A' : '#D4C9B8'}`,
                  borderBottom: isOn ? 'none' : undefined,
                }}
                data-testid={`toggle-${p.key}`}
              >
                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isOn ? '#2C2520' : '#EDE8DF' }}>
                  <Icon size={14} color={isOn ? '#F0EBE3' : '#8C7E6E'} />
                </div>
                <span className="flex-1 text-[13px] font-medium" style={{ color: '#3C3226' }}>{p.name}</span>
                <div className="w-4 h-4 flex items-center justify-center" style={{ border: `1.5px solid ${isOn ? '#1D6B4F' : '#D4C9B8'}`, backgroundColor: isOn ? '#1D6B4F' : 'transparent' }}>
                  {isOn && <CheckCircle2 size={10} color="#F0EBE3" />}
                </div>
              </button>
              {isOn && (
                <div className="px-4 pb-3 pt-2" style={{ backgroundColor: '#FDFBF7', borderLeft: '1px solid #B8985A', borderRight: '1px solid #B8985A', borderBottom: '1px solid #B8985A' }}>
                  <input
                    type="url"
                    value={formData.social_profiles[p.key] || ''}
                    onChange={e => setUrl(p.key, e.target.value)}
                    placeholder={p.placeholder}
                    className="w-full px-4 py-3 text-sm outline-none"
                    style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8', color: '#3C3226' }}
                    onFocus={e => e.target.style.borderColor = '#B8985A'}
                    onBlur={e => e.target.style.borderColor = '#D4C9B8'}
                    data-testid={`url-${p.key}`}
                    autoFocus
                  />
                  <p className="text-[10px] mt-1" style={{ color: '#8C7E6E' }}>e.g. {p.example}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="fixed bottom-0 left-0 right-0 lg:left-1/2 p-6" style={{ backgroundColor: 'rgba(247,244,239,0.95)', borderTop: '1px solid #D4C9B8' }}>
        <button
          onClick={onNext}
          className="w-full py-4 text-[12px] font-medium uppercase tracking-widest flex items-center justify-center gap-2 transition-opacity"
          style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
          data-testid="run-scan-btn"
        >
          {filledCount > 0 ? `Run my scan (${filledCount} profile${filledCount > 1 ? 's' : ''})` : 'Run my scan'} <Search size={14} />
        </button>
        <button onClick={onSkip} className="w-full mt-2 py-2 text-[12px] text-center" style={{ color: '#8C7E6E' }} data-testid="skip-profiles-btn">
          Skip for now
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   STEP 5: SCANNING ANIMATION
   Dynamic queries based on the user's actual
   profession and city. No more hardcoded plumber.
   ══════════════════════════════════════════ */
function ScanningScreen({ scanStatus, formData }) {
  const [activeIdx, setActiveIdx] = useState(-1);
  const [completed, setCompleted] = useState([]);
  const [currentQuery, setCurrentQuery] = useState('');
  const [phase, setPhase] = useState('intro'); // intro → scanning → finishing
  const timersRef = useRef([]);

  const primaryProf =
    formData.professions?.find(p => p.primary)?.name ||
    formData.professions?.[0]?.name ||
    'your business';
  const city = (formData.city || 'your area').split(',')[0].trim();
  const bName = formData.business_name || 'Your Business';

  const QUERIES = [
    `Best ${primaryProf} in ${city}`,
    `Who is the best ${primaryProf} in ${city}?`,
    `Top ${primaryProf} recommendations in ${city}`,
    `${primaryProf} services near ${city}`,
    `Best ${primaryProf} to hire in ${city}`,
    `${city} ${primaryProf} reviews`,
    `Who do you recommend for ${primaryProf} in ${city}?`,
    `Best local ${primaryProf} in ${city}`,
    `${primaryProf} near me in ${city}`,
  ];

  const RESULTS = [
    'Mentioned — Position #2',
    'Not found',
    'Mentioned — Position #4',
    'Not found',
    'Mentioned — Position #1',
    'Not found',
    'Mentioned — Position #3',
    'Not found',
    'Not found',
  ];

  const schedule = (fn, delay) => {
    const id = setTimeout(fn, delay);
    timersRef.current.push(id);
  };

  useEffect(() => {
    // Theatrical intro phase
    schedule(() => setPhase('scanning'), 1800);

    let idx = 0;
    const activate = () => {
      if (idx >= AI_ENGINES.length) {
        schedule(() => setPhase('finishing'), 1000);
        return;
      }
      setActiveIdx(idx);
      setCurrentQuery(`Asking ${AI_ENGINES[idx]}: "${QUERIES[idx]}"`);
      const currentIdx = idx;
      schedule(() => {
        setCompleted(prev => [...prev, { idx: currentIdx, result: RESULTS[currentIdx] }]);
      }, 2500 + Math.random() * 1500);
      idx++;
      if (idx < AI_ENGINES.length) {
        schedule(activate, 3500 + Math.random() * 1500);
      } else {
        schedule(() => setPhase('finishing'), 3500 + 2000);
      }
    };
    schedule(activate, 2500);
    return () => timersRef.current.forEach(clearTimeout);
  }, []);

  const completedCount = completed.length;
  const progress = (completedCount / AI_ENGINES.length) * 100;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden" style={{ backgroundColor: '#2C2520' }} data-testid="scanning-screen">
      {/* Ambient background pulse */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ opacity: [0.02, 0.06, 0.02] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'radial-gradient(ellipse at center, rgba(184,152,90,0.15) 0%, transparent 70%)' }}
      />

      {/* Intro → Business name with dramatic reveal */}
      <motion.p
        initial={{ opacity: 0, y: 20, letterSpacing: '0.4em' }}
        animate={{ opacity: 1, y: 0, letterSpacing: '0.15em' }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="mb-2 relative z-10"
        style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', color: '#F0EBE3', fontWeight: 300, textTransform: 'uppercase' }}
      >
        {bName}
      </motion.p>
      <motion.p
        initial={{ opacity: 0, width: 0 }}
        animate={{ opacity: 1, width: '80px' }}
        transition={{ delay: 0.6, duration: 0.8 }}
        className="h-[1px] mb-6"
        style={{ backgroundColor: '#B8985A' }}
      />
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.0 }}
        className="mb-10 relative z-10"
        style={{ fontSize: '13px', color: '#8C7E6E', letterSpacing: '0.05em' }}
        data-testid="scan-profession-label"
      >
        Scanning 9 AI engines for: {primaryProf} in {city}
      </motion.p>

      {/* 3x3 Engine Grid — theatrical activation */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="grid grid-cols-3 gap-3 mb-8 w-full max-w-[420px] relative z-10"
      >
        {AI_ENGINES.map((engine, i) => {
          const isActive = i === activeIdx;
          const doneEntry = completed.find(c => c.idx === i);
          const isDone = !!doneEntry;
          const mentioned = doneEntry?.result?.startsWith('Mentioned');

          return (
            <motion.div
              key={engine}
              className="flex flex-col items-center justify-center relative"
              style={{
                height: '68px',
                width: '100%',
                backgroundColor: isActive ? 'rgba(184,152,90,0.12)' : isDone ? 'rgba(247,244,239,0.06)' : 'rgba(247,244,239,0.03)',
                borderRadius: '4px',
                border: isActive ? '1px solid rgba(184,152,90,0.4)' : isDone ? `1px solid ${mentioned ? 'rgba(29,107,79,0.3)' : 'rgba(198,122,92,0.3)'}` : '1px solid rgba(247,244,239,0.06)',
                transition: 'all 0.4s ease',
              }}
              animate={isActive ? { boxShadow: ['0 0 0px rgba(184,152,90,0)', '0 0 20px rgba(184,152,90,0.15)', '0 0 0px rgba(184,152,90,0)'] } : {}}
              transition={isActive ? { duration: 1.5, repeat: Infinity } : {}}
              data-testid={`scan-engine-${i}`}
            >
              {isActive && (
                <motion.div
                  className="absolute inset-0 rounded"
                  animate={{ opacity: [0, 0.1, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  style={{ backgroundColor: '#B8985A' }}
                />
              )}
              <span className="text-[12px] font-medium relative z-10" style={{ color: isActive ? '#F0EBE3' : isDone ? '#F0EBE3' : '#6B5F53' }}>{engine}</span>
              <span className="text-[10px] mt-1 relative z-10" style={{ color: isActive ? '#B8985A' : isDone ? (mentioned ? '#1D6B4F' : '#C67A5C') : '#4A3F36' }}>
                {isActive ? 'Querying...' : isDone ? doneEntry.result : 'Waiting'}
              </span>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Live query feed */}
      <AnimatePresence mode="wait">
        {currentQuery && (
          <motion.p
            key={currentQuery}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="text-center mb-6 max-w-md relative z-10"
            style={{ fontSize: '12px', color: '#8C7E6E', fontStyle: 'italic' }}
            data-testid="live-query-feed"
          >
            {currentQuery}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Progress bar */}
      <div className="w-full max-w-[420px] mb-4 relative z-10">
        <div className="h-[2px] w-full" style={{ backgroundColor: 'rgba(247,244,239,0.08)' }}>
          <motion.div
            className="h-full"
            style={{ backgroundColor: '#B8985A' }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Running counter */}
      <p className="text-[14px] relative z-10" style={{ color: '#F0EBE3' }} data-testid="scan-counter">
        {completedCount} of {AI_ENGINES.length} engines scanned
      </p>

      <p className="mt-6 relative z-10" style={{ fontSize: '11px', color: '#5C5347' }} data-testid="scan-search-label">
        Searching for the best {primaryProf} in {city}
      </p>
    </div>
  );
}

/* ══════════════════════════════════════════
   STEP 6: RESULTS
   ══════════════════════════════════════════ */
function ResultsScreen({ scanResults, navigate }) {
  const score = scanResults?.score || {};
  const gaps = scanResults?.gaps || [];
  const [displayScore, setDisplayScore] = useState(0);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const finalScore = score.total || 0;

  useEffect(() => {
    // Count-up animation
    const duration = 2000;
    const steps = 60;
    const increment = finalScore / steps;
    let current = 0;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      current = Math.min(Math.round(increment * step), finalScore);
      setDisplayScore(current);
      if (step >= steps) {
        clearInterval(timer);
        setDisplayScore(finalScore);
        // Fire confetti burst
        const colors = ['#B8985A', '#1D6B4F', '#F0EBE3', '#C67A5C'];
        confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors, gravity: 0.8, ticks: 150 });
        setTimeout(() => confetti({ particleCount: 40, spread: 50, origin: { x: 0.3, y: 0.5 }, colors, gravity: 0.8, ticks: 100 }), 300);
        setTimeout(() => confetti({ particleCount: 40, spread: 50, origin: { x: 0.7, y: 0.5 }, colors, gravity: 0.8, ticks: 100 }), 500);
        // Show breakdown after celebration
        setTimeout(() => setShowBreakdown(true), 800);
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [finalScore]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" style={{ backgroundColor: '#F7F4EF' }} data-testid="results-screen">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="text-center">
        <ScoreDisplay score={displayScore} color="#1D6B4F" size="large" />

        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-[22px] font-semibold mt-6 mb-2"
          style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}
        >
          Here's what AI is saying about you
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-sm max-w-md mx-auto mb-8"
          style={{ color: '#5C5347' }}
        >
          {score.interpretation || 'This is how AI engines see your business right now.'}
        </motion.p>

        {/* Breakdown bars — staggered reveal */}
        {showBreakdown && score.breakdown && (
          <div className="max-w-sm mx-auto space-y-3 mb-10">
            {Object.entries(score.breakdown).map(([key, val], idx) => {
              const scoreVal = typeof val === 'object' ? (val.score ?? 0) : (val ?? 0);
              const label = typeof val === 'object' && val.label ? val.label : key.replace(/_/g, ' ');
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.15, duration: 0.4 }}
                >
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="uppercase tracking-wider" style={{ color: '#5C5347' }}>{label}</span>
                    <span className="font-semibold" style={{ color: '#3C3226' }}>{scoreVal}/100</span>
                  </div>
                  <div className="h-1.5 w-full" style={{ backgroundColor: '#D4C9B8' }}>
                    <motion.div
                      className="h-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${scoreVal}%` }}
                      transition={{ delay: idx * 0.15 + 0.2, duration: 0.6, ease: 'easeOut' }}
                      style={{ backgroundColor: '#1D6B4F' }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Top gaps */}
        {gaps.length > 0 && (
          <div className="max-w-sm mx-auto mb-10 text-left">
            <p className="text-[11px] font-medium uppercase tracking-widest mb-3" style={{ color: '#5C5347' }}>Top opportunities</p>
            <div className="space-y-2">
              {gaps.slice(0, 3).map((gap, i) => (
                <div key={i} className="flex items-start gap-3 p-3" style={{ border: '1px solid #D4C9B8', backgroundColor: '#FDFBF7' }}>
                  <span className="text-[12px] font-semibold" style={{ color: '#1D6B4F' }}>+{gap.points || gap.potential_score_boost || 5}</span>
                  <div>
                    <p className="text-[13px] font-medium" style={{ color: '#3C3226' }}>{gap.title || gap.action}</p>
                    <p className="text-[11px]" style={{ color: '#8C7E6E' }}>{gap.description || ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTAs */}
        <button
          onClick={() => navigate('/login?action=register')}
          className="px-10 py-4 text-[12px] font-medium uppercase tracking-widest transition-opacity hover:opacity-90"
          style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
          data-testid="claim-seat-btn"
        >
          Claim your seat <ArrowRight size={14} className="inline ml-1" />
        </button>
        <p className="text-[12px] mt-4" style={{ color: '#8C7E6E' }}>
          Create a free account to save your report.
        </p>
      </motion.div>
    </div>
  );
}
